<template>
  <es-loader v-if="loading"/>
  <es-exception
    v-else-if="loadingError"
    exceptionMessage="There was an issue.
      Please try again or contact your administrator."
  />
  <es-exception
    v-else-if="!students.length"
    exceptionMessage="No results were found."
  />
  <div v-else class="student-container">
    <div class="is-flex has-text-weight-bold text-grey">
      Identified Students
    </div>
    <table class="table is-hoverable is-fullwidth">
      <es-sorter
        :apiSorter="apiSorter"
        :columnHeaders="columnHeaders"
        @updateSorter="updateSorter"
      />
      <tbody>
        <tr v-for="(student, index) in students" :key="index">
          <td>
            {{ student.schoolCode }}
          </td>
          <td>
            {{ formatStateID(student.stateStudentId) }}
          </td>
          <td v-for="program in programs" :key="program">
            <div v-for="(p , i) in student.studentPrograms" :key="i">
              <system-match-indicator
                v-if="p.programDescription === program
                  && (!p.matchTypeCode || p.matchTypeCode !== 'MANL')
                  && p.enrolled === true"
                :title="`This is match reason for ${p.programDescription}`"
              />
              <manual-match-indicator
                v-if="p.programDescription === program
                  && p.matchTypeCode && p.matchTypeCode === 'MANL'
                  && p.enrolled === true"
                :title="p.matchReason
                  ? `Match Reason: ${p.matchReason}`
                  : false"
              />
            </div>
          </td>
          <td>
            {{ student.programCount }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="loadingMore" style="padding: 0.5rem 0">
      <es-loader size="2x"/>
    </div>
    <a
      v-else-if="canLoadMore"
      style="padding: 0.5rem 0"
      @click="loadMore"
    >
      Load More
    </a>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import EsLoader from '@/components/EsLoader.vue';
import ManualMatchIndicator from '@/components/ManualMatchIndicator.vue';
import SystemMatchIndicator from '@/components/SystemMatchIndicator.vue';
import EsSorter from '@/components/EsSorter.vue';
import EsException from '@/components/EsException.vue';
import StateIdUtils from '@/mixins/StateIdUtils';

export default {
  mixins: [
    StateIdUtils,
  ],
  props: {
    cluster: {
      type: Boolean,
    },
    locationGroupId: {
      type: String,
    },
    schoolYear: {
      type: Number,
    },
    stateId: {
      type: Number,
    },
    cepTab: {
      type: String,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 25,
    },
    sortField: {
      type: String,
    },
    sortOrder: {
      type: String,
    },
    columnHeaders: {
      type: Array,
    },
  },
  components: {
    EsLoader,
    ManualMatchIndicator,
    SystemMatchIndicator,
    EsSorter,
    EsException,
  },
  data() {
    return {
      loadingMore: null,
      loading: null,
      loadingError: null,
      students: [],
      programs: [],
      totalCount: 0,
      apiSorter: {},
    };
  },
  computed: {
    canLoadMore() {
      return (this.page * this.pageSize) < this.totalCount;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    urlParams() {
      const params = {};
      if (this.cluster) {
        params.clusterId = this.stateId;
      } else {
        params.schoolId = this.stateId;
      }
      params.pageNum = this.page ? this.page : undefined;
      params.pageSize = this.pageSize ? this.pageSize : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);
      return params;
    },
    updatedSorter() {
      return `${this.sortField}:${this.sortOrder}`;
    },
  },
  created() {
    this.getStudents();
  },
  watch: {
    updatedSorter() {
      this.getStudents();
    },
  },
  methods: {
    async getStudents() {
      try {
        this.loading = true;
        this.loadingError = false;
        const tab = this.cepTab && this.cepTab.includes('elections') ? 'elections' : this.cepTab;
        const res = await axios.get(`cep/${this.locationGroupId}/${tab}/${this.schoolYear}/identified-students`,
          {
            params: this.urlParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        this.students = res.data.data;
        this.totalCount = res.data.totalCount;
        [this.apiSorter] = res.data.sorters;
        if (this.students.length > 0) {
          const sp = this.students.map(d => d.studentPrograms);
          this.programs = [...new Set(sp.flat().map(s => s.programDescription))];
          this.$emit('updateStudentProgramHeaders', this.programs);
        }
      } catch (error) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      this.page = this.page + 1;
      try {
        this.loadingMore = true;
        const tab = this.cepTab && this.cepTab.includes('elections') ? 'elections' : this.cepTab;
        const res = await axios.get(`cep/${this.locationGroupId}/${tab}/${this.schoolYear}/identified-students`,
          {
            params: this.urlParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        res.data.data.forEach(obj => this.students.push(obj));
      } catch (e) {
        this.page = 1;
        this.students = [];
        this.programs = [];
        this.getStudents();
      } finally {
        this.loadingMore = false;
      }
    },
    updateSorter() {
      this.$emit('updateSorter');
    },
  },
};
</script>

<style lang="sass" scoped>
  .student-container
    font-size: 14px
  .is-flex
    border-bottom: 1px solid #ddd
  .text-grey
    color: #555
  .sort-by
    padding-right: 18px
    position: relative
    color: #777
    text-decoration: none
    -webkit-appearance: slider-vertical
    &:before, &:after
      border: 4px solid transparent
      content: ""
      display: block
      height: 0
      right: 5px
      top: 50%
      position: absolute
      width: 0
    &:before
      border-bottom-color: #999
      margin-top: -9px
    &:after
      border-top-color: #999
      margin-top: 1px
</style>
